// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedChargeCascadeListProperty } from '@/audit/api/types';
import { ExpandMoreRounded } from '@/constants/hardfin-standard-icons';
import { pluralize } from '@/utilities/pluralize';
import { Box, ButtonBase, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { memo, useCallback, useMemo, useState } from 'react';

type CascadeEntry = NonNullable<
  CachedChargeCascadeListProperty['newValue']
>[number];

interface Props {
  property: CachedChargeCascadeListProperty;
}

interface BillDateGroup {
  dateKey: string;
  entries: CascadeEntry[];
}

function groupByBillDate(entries: CascadeEntry[]): BillDateGroup[] {
  const groups = new Map<string, CascadeEntry[]>();

  for (const entry of entries) {
    const key = entry.expectedBillableDate ?? 'Unscheduled';
    const existing = groups.get(key);
    if (existing) {
      existing.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }

  const result = Array.from(groups.entries()).map(([dateKey, grouped]) => ({
    dateKey,
    entries: grouped,
  }));

  // Sort chronologically, unscheduled last
  result.sort((a, b) => {
    if (a.dateKey === 'Unscheduled') return 1;
    if (b.dateKey === 'Unscheduled') return -1;
    return a.dateKey.localeCompare(b.dateKey);
  });

  return result;
}

export function AuditChargeCascadeDetails(props: Props) {
  const groups = useMemo(
    () => groupByBillDate(props.property.newValue ?? []),
    [props.property.newValue]
  );

  if (groups.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {groups.map((group) => (
        <BillDateSection
          key={group.dateKey}
          dateKey={group.dateKey}
          entries={group.entries}
        />
      ))}
    </Box>
  );
}

const BillDateSection = memo(function BillDateSection(props: {
  dateKey: string;
  entries: CascadeEntry[];
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  // Prefix scheduled groups with "Expected billable date:" so the header
  // names what the date actually represents. Unscheduled groups have no
  // date to qualify, so they stand alone.
  const dateLabel = useMemo(() => {
    if (props.dateKey === 'Unscheduled') return 'Unscheduled';
    const formatted = DateTime.fromISO(props.dateKey).toFormat('LLL d, yyyy');
    return `Expected billable date: ${formatted}`;
  }, [props.dateKey]);

  // Collapsed fast-exit — don't render charge rows
  if (!isExpanded) {
    return (
      <ButtonBase
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          ml: 2,
          py: 0.25,
          borderRadius: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ExpandMoreRounded
          sx={{ fontSize: 16, transition: 'transform 150ms' }}
          color="action"
        />
        <Typography variant="caption" fontWeight="bold">
          {dateLabel} ({pluralize(props.entries.length, { singular: 'charge' })}
          )
        </Typography>
      </ButtonBase>
    );
  }

  return (
    <Box sx={{ ml: 2 }}>
      <ButtonBase
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ExpandMoreRounded
          sx={{
            fontSize: 16,
            transform: 'rotate(180deg)',
            transition: 'transform 150ms',
          }}
          color="action"
        />
        <Typography variant="caption" fontWeight="bold">
          {dateLabel} ({pluralize(props.entries.length, { singular: 'charge' })}
          )
        </Typography>
      </ButtonBase>
      <Box sx={{ ml: 3 }}>
        {props.entries.map((entry) => (
          <ChargeEntryRow key={entry.chargeContext.key} entry={entry} />
        ))}
      </Box>
    </Box>
  );
});

const ChargeEntryRow = memo(function ChargeEntryRow(props: {
  entry: CascadeEntry;
}) {
  const sku = props.entry.itemContext.sku;
  const name = props.entry.itemContext.name;
  const qty = props.entry.quantity;
  // TotalPrice / unitPrice arrive as formatted currency strings from
  // auditCurrencyValueSchema.
  const total = props.entry.totalPrice;

  // PricingMode and unitPrice were added to the cascade audit payload
  // after it started shipping, so legacy audit rows have them as
  // `undefined`. Current FIXED-mode charges legitimately carry a `null`
  // unitPrice because FIXED pricing has no unit-price concept. We use
  // these two states as distinct signals:
  //   - pricingMode === undefined → legacy row, suppress the "@ …"
  //     segment entirely (we cannot guess whether the concept applies).
  //   - pricingMode === 'PER_UNIT' && unitPrice → render "@ $X/unit".
  //   - pricingMode === 'FIXED' → render nothing after the item name;
  //     the "= <total>" tail still carries the price information.
  const unitsLabel = pluralize(qty, { singular: 'unit' });
  const pricingMode = props.entry.pricingMode;
  const unitPrice = props.entry.unitPrice ?? null;
  const pricePart =
    pricingMode === 'PER_UNIT' && unitPrice !== null
      ? ` @ ${unitPrice}/unit`
      : '';

  return (
    <Typography variant="caption" display="block">
      {`${unitsLabel} \u00d7 ${sku} (${name})${pricePart} = ${total}`}
    </Typography>
  );
});
